<script setup lang="ts">
import { isNumber } from '../../utils'

import type { TriggerSettings } from '../../types/marketingFlow'

const { t } = useI18n()

const { getTriggerStepErrors } = useMarketingFlow()

const triggerSettings = defineModel<TriggerSettings>('triggerSettings')
const hasCooldown = ref<boolean>(false)
const hasMaxTriggersPerUser = ref<boolean>(false)

const cooldownDays = ref<string | null>(null)

const emit = defineEmits<{
  (e: 'onChanges', { isCritical, field, value }): void
}>()

const triggerStepErrors = computed(() => {
  return getTriggerStepErrors(triggerSettings.value)
})

const maxTriggersPerUserError = computed(() => {
  const maxTriggersPerUserErrors = triggerStepErrors.value.find((error) => error.field === 'maxTriggersPerUser')

  return maxTriggersPerUserErrors
    ? [t(`pages.marketingFlow.marketingFlowEditPage.error.${maxTriggersPerUserErrors?.i18nKey}`)]
    : null
})

const cooldownDaysError = computed(() => {
  const cooldownMinutesErrors = triggerStepErrors.value.find((error) => error.field === 'cooldownMinutes')

  return cooldownMinutesErrors
    ? [t(`pages.marketingFlow.marketingFlowEditPage.error.${cooldownMinutesErrors?.i18nKey}`)]
    : null
})

const handleMultipleTimeScenario = (hasMultipleTimeScenario: boolean) => {
  hasMaxTriggersPerUser.value = false
  hasCooldown.value = false
  triggerSettings.value.cooldownMinutes = null

  if (hasMultipleTimeScenario) {
    triggerSettings.value.maxTriggersPerUser = null
  } else {
    triggerSettings.value.maxTriggersPerUser = 1
  }

  emit('onChanges', { isCritical: true, field: 'multipleTimeScenario', value: hasMultipleTimeScenario })
}

const handleAddMaxTriggersPerUser = () => {
  hasMaxTriggersPerUser.value = true
  triggerSettings.value.maxTriggersPerUser = null
}

const updateCooldownMinutes = (coolDownDays: number) => {
  if (isNumber(coolDownDays)) {
    triggerSettings.value.cooldownMinutes = coolDownDays * 24 * 60
  } else {
    triggerSettings.value.cooldownMinutes = 'invalid_number'
  }

  emit('onChanges', { isCritical: true, field: 'cooldownMinutes', value: triggerSettings.value.cooldownMinutes })
}

const updateMaxTriggersPerUser = (maxTriggersPerUser: number) => {
  emit('onChanges', { isCritical: true, field: 'maxTriggersPerUser', value: maxTriggersPerUser })
}

const removeMaxTriggersPerUser = () => {
  hasMaxTriggersPerUser.value = false
  triggerSettings.value.maxTriggersPerUser = null

  emit('onChanges', { isCritical: true, field: 'maxTriggersPerUser', value: triggerSettings.value.maxTriggersPerUser })
}

const removeCooldown = () => {
  hasCooldown.value = false
  triggerSettings.value.cooldownMinutes = null
  emit('onChanges', { isCritical: true, field: 'cooldownMinutes', value: triggerSettings.value.cooldownMinutes })
}

onMounted(async () => {
  hasMaxTriggersPerUser.value = Number(triggerSettings.value.maxTriggersPerUser) > 1
  hasCooldown.value = Number(triggerSettings.value.cooldownMinutes) > 0

  cooldownDays.value = triggerSettings.value.cooldownMinutes
    ? (Number(triggerSettings.value.cooldownMinutes) / 24 / 60).toString()
    : null
})
</script>

<template>
  <div class="flex w-full flex-col px-5 pb-4">
    <div class="flex flex-col rounded-lg border bg-muted p-4">
      <p class="mb-4 font-medium text-foreground">
        {{ t('global.words.options') }}
      </p>

      <div class="flex flex-row items-center">
        <div class="mr-2 flex flex-1">
          <span class="text-sm text-foreground">
            {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.runnableMultipleTimes') }}
          </span>
        </div>
        <BaseSwitch
          id="sendAtSpecificHour"
          v-model="triggerSettings.multipleTimeScenario"
          @update:model-value="handleMultipleTimeScenario"
        />
      </div>

      <div
        v-if="triggerSettings.multipleTimeScenario && !hasCooldown"
        class="mt-4 flex cursor-pointer flex-row text-primary hover:text-primary/80"
        @click="hasCooldown = true"
      >
        <BaseIcon name="HeroPlusIconOutline" class="mr-2.5 h-5 w-5" />
        <span class="text-sm font-medium">{{
          t('pages.marketingFlow.marketingFlowEditPage.trigger.addCooldown')
        }}</span>
      </div>

      <div v-if="triggerSettings.multipleTimeScenario && hasCooldown" class="flex w-full flex-col mt-4 gap-1">
        <label class="max-w-max text-sm text-foreground">
          {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.every') }}
        </label>

        <div class="flex flex-row items-start">
          <AppFormInput
            id="field"
            v-model="cooldownDays"
            class="flex-1"
            :errors="cooldownDaysError"
            error-classes="text-xs"
            @update:model-value="updateCooldownMinutes"
          >
            <template #icon-right>
              <span class="text-sm text-muted-foreground">{{ t('global.words.days') }}</span>
            </template>
          </AppFormInput>

          <div class="flex h-10 items-center justify-center pl-3">
            <BaseIcon
              name="HeroTrashIconOutline"
              class="h-6 w-6 cursor-pointer text-foreground-secondary hover:text-red-600"
              @click="removeCooldown"
            />
          </div>
        </div>
      </div>

      <div
        v-if="triggerSettings.multipleTimeScenario && !hasMaxTriggersPerUser"
        class="mt-4 flex cursor-pointer flex-row text-primary hover:text-primary/80"
        @click="handleAddMaxTriggersPerUser"
      >
        <BaseIcon name="HeroPlusIconOutline" class="mr-2.5 h-5 w-5" />
        <span class="text-sm font-medium">{{
          t('pages.marketingFlow.marketingFlowEditPage.trigger.addMaxTriggers')
        }}</span>
      </div>

      <div v-if="triggerSettings.multipleTimeScenario && hasMaxTriggersPerUser" class="flex w-full flex-col mt-4 gap-1">
        <label class="max-w-max text-sm text-foreground">
          {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.maxTriggersPerUser') }}
        </label>

        <div class="flex flex-row items-start">
          <AppFormInput
            id="field"
            v-model="triggerSettings.maxTriggersPerUser"
            class="flex-1"
            :errors="maxTriggersPerUserError"
            error-classes="text-xs"
            @update:model-value="updateMaxTriggersPerUser"
          >
            <template #icon-right>
              <span class="text-sm text-muted-foreground">{{ t('global.words.times') }}</span>
            </template>
          </AppFormInput>
          <div class="flex h-10 items-center justify-center pl-3">
            <BaseIcon
              name="HeroTrashIconOutline"
              class="h-6 w-6 cursor-pointer text-foreground-secondary hover:text-red-600"
              @click="removeMaxTriggersPerUser"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
